import type {Polygon,MultiPolygon,Position} from 'geojson';
import {union} from '@turf/union';
import {feature,featureCollection} from '@turf/helpers';
import {booleanPointInPolygon} from '@turf/boolean-point-in-polygon';
import type {NeighborParcel} from './neighborParcels';

type Box=[number,number,number,number];
type Pair=[number,number];
export interface NeighborOwnershipPart {id:string;geometry:Polygon;bbox:Box;labelPoint:Pair|null}
export interface NeighborOwnershipGroup {
 id:string;identity:string|null;records:NeighborParcel[];ownerNames:string[];
 geometry:Polygon|MultiPolygon;bbox:Box;parts:NeighborOwnershipPart[];merged:boolean;
}

// This is an equality key for recorded data, not a claim about legal identity.
// Requiring the complete recorded owner set AND mailing address avoids joining
// two unrelated people with the same name. Missing values remain separate.
const normalized=(value:string)=>value.normalize('NFKC').trim().toLocaleUpperCase('en-US').replace(/[.,]/g,' ').replace(/\s+/g,' ');
export function neighborOwnerIdentity(record:NeighborParcel):string|null{
 const names=record.ownerNames?.map(normalized).filter(Boolean).sort(),mail=normalized(record.mailingAddress||'');
 const unknown=(value:string)=>['UNKNOWN','UNAVAILABLE','NOT AVAILABLE','N/A','NA'].includes(value);
 if(!names?.length||names.some(unknown)||!mail||unknown(mail)||!record.countyFips)return null;
 return JSON.stringify([record.countyFips,[...new Set(names)],mail]);
}
const polygons=(geometry:Polygon|MultiPolygon):Position[][][]=>geometry.type==='Polygon'?[geometry.coordinates]:geometry.coordinates;
function boxOf(geometry:Polygon|MultiPolygon):Box{
 let west=Infinity,south=Infinity,east=-Infinity,north=-Infinity;
 for(const polygon of polygons(geometry))for(const ring of polygon)for(const pair of ring){if(!Number.isFinite(pair[0])||!Number.isFinite(pair[1]))continue;west=Math.min(west,pair[0]);south=Math.min(south,pair[1]);east=Math.max(east,pair[0]);north=Math.max(north,pair[1]);}
 return [west,south,east,north];
}
function usable(geometry:Polygon|MultiPolygon){
 try{return polygons(geometry).length>0&&polygons(geometry).every(polygon=>polygon.length>0&&polygon.every(ring=>ring.length>=4&&ring.every(pair=>pair.length>=2&&Number.isFinite(pair[0])&&Number.isFinite(pair[1]))&&ring[0][0]===ring[ring.length-1][0]&&ring[0][1]===ring[ring.length-1][1]));}catch{return false;}
}
const hasPoint=(shape:Polygon|MultiPolygon,point:Pair,strict=false)=>{try{return booleanPointInPolygon(point,shape,{ignoreBoundary:strict});}catch{return false;}};
function stableKey(keys:string[]){
 let hash=2166136261;for(const character of keys.join('\0')){hash^=character.charCodeAt(0);hash=Math.imul(hash,16777619);}
 return 'holding:'+keys[0]+':'+(hash>>>0).toString(36);
}

/** A bounded interior search: unlike a bbox center it respects concavities and
 * holes. Coordinates are projected locally so longitudes and latitudes have
 * comparable scale. No label is better than placing one outside its holding. */
function interiorPoint(geometry:Polygon,box:Box):Pair|null{
 if(!usable(geometry)||!box.every(Number.isFinite))return null;
 const [west,south,east,north]=box,scale=Math.max(.01,Math.cos((south+north)/2*Math.PI/180));
 const width=(east-west)*scale,height=north-south;if(width<=0||height<=0)return null;
 const rings=geometry.coordinates.map(ring=>ring.map(pair=>[(pair[0]-west)*scale,pair[1]-south] as Pair));
 const position=(x:number,y:number):Pair=>[west+x/scale,south+y];
 const distance=(x:number,y:number)=>{
  let minimum=Infinity;
  for(const ring of rings)for(let i=0,j=ring.length-1;i<ring.length;j=i++){
   const a=ring[j],b=ring[i],dx=b[0]-a[0],dy=b[1]-a[1],length=dx*dx+dy*dy,t=length?Math.max(0,Math.min(1,((x-a[0])*dx+(y-a[1])*dy)/length)):0;
   minimum=Math.min(minimum,(x-a[0]-t*dx)**2+(y-a[1]-t*dy)**2);
  }
  const value=Math.sqrt(minimum);return hasPoint(geometry,position(x,y),true)?value:-value;
 };
 type Cell={x:number;y:number;hx:number;hy:number;distance:number;max:number};
 const cell=(x:number,y:number,hx:number,hy:number):Cell=>{const d=distance(x,y);return {x,y,hx,hy,distance:d,max:d+Math.hypot(hx,hy)};};
 const queue:Cell[]=[cell(width/2,height/2,width/2,height/2)];let best=queue[0];
 // The outer-ring centroid is a good early candidate for narrow/irregular land.
 let twiceArea=0,cx=0,cy=0;const outer=rings[0];
 for(let i=0,j=outer.length-1;i<outer.length;j=i++){const a=outer[j],b=outer[i],cross=a[0]*b[1]-b[0]*a[1];twiceArea+=cross;cx+=(a[0]+b[0])*cross;cy+=(a[1]+b[1])*cross;}
 if(twiceArea){const candidate=cell(cx/(3*twiceArea),cy/(3*twiceArea),0,0);if(candidate.distance>best.distance)best=candidate;}
 const precision=Math.min(width,height)/40;
 // Hard caps keep work bounded even for a pathological valid county shape.
 let evaluated=2;
 while(queue.length&&evaluated<162){
  queue.sort((a,b)=>b.max-a.max);const current=queue.shift()!;
  if(current.distance>best.distance)best=current;
  if(current.max-best.distance<=precision)continue;
  const hx=current.hx/2,hy=current.hy/2;
  for(const dx of [-hx,hx])for(const dy of [-hy,hy]){const child=cell(current.x+dx,current.y+dy,hx,hy);if(child.distance>best.distance)best=child;queue.push(child);evaluated++;}
 }
 const result=position(best.x,best.y);return best.distance>0&&hasPoint(geometry,result,true)?result:null;
}
function displayGroup(records:NeighborParcel[],identity:string|null,geometry:Polygon|MultiPolygon,merged:boolean):NeighborOwnershipGroup{
 const id=stableKey(records.map(record=>record.parcelKey).sort()),bbox=boxOf(geometry);
 const parts=polygons(geometry).map((coordinates,index)=>{const part:Polygon={type:'Polygon',coordinates},bbox=boxOf(part);return {id:id+':'+index,geometry:part,bbox,labelPoint:interiorPoint(part,bbox)};});
 return {id,identity,records,ownerNames:[...records[0].ownerNames],geometry,bbox,parts,merged};
}

/** Build display-only ownership outlines once when snapshot records change.
 * Original parcels, their geometry and IDs are retained for details/contacts.
 * A failed/oversized union falls back to every original parcel; no record is
 * omitted and no clipping, buffering or coordinate rounding joins real gaps. */
export function buildNeighborOwnership(records:NeighborParcel[]):NeighborOwnershipGroup[]{
 const buckets=new Map<string,NeighborParcel[]>();
 for(const record of records){const identity=neighborOwnerIdentity(record),key=identity||'parcel:'+record.parcelKey;const bucket=buckets.get(key);if(bucket)bucket.push(record);else buckets.set(key,[record]);}
 const groups:NeighborOwnershipGroup[]=[];
 for(const bucket of buckets.values()){
  const sorted=[...bucket].sort((a,b)=>a.parcelKey.localeCompare(b.parcelKey)),identity=neighborOwnerIdentity(sorted[0]);
  if(sorted.length===1){groups.push(displayGroup(sorted,identity,sorted[0].geometry,false));continue;}
  const count=sorted.reduce((total,record)=>total+polygons(record.geometry).reduce((sum,polygon)=>sum+polygon.reduce((n,ring)=>n+ring.length,0),0),0);
  if(sorted.length<=500&&count<=50000&&sorted.every(record=>usable(record.geometry))){
   try{const result=union(featureCollection(sorted.map(record=>feature(record.geometry))));if(result?.geometry&&usable(result.geometry)){groups.push(displayGroup(sorted,identity,result.geometry,true));continue;}}catch{/* Retain original boundaries if display union cannot be completed. */}
  }
  for(const record of sorted)groups.push(displayGroup([record],neighborOwnerIdentity(record),record.geometry,false));
 }
 return groups.sort((a,b)=>a.id.localeCompare(b.id));
}

/** A merged outline never invents a parcel/contact target. Boundary ambiguity
 * returns null so callers can ask the viewer to choose an original parcel. */
export function neighborParcelAtPoint(group:NeighborOwnershipGroup,point:Pair):NeighborParcel|null{
 if(!point.every(Number.isFinite))return null;
 const matches=group.records.filter(record=>hasPoint(record.geometry,point,true));
 if(matches.length===1)return matches[0];
 if(matches.length>1)return null;
 const edge=group.records.filter(record=>hasPoint(record.geometry,point));return edge.length===1?edge[0]:null;
}
